import type { QueryClient } from '@tanstack/react-query';

/**
 * Helpers de invalidación de caché tras mutaciones.
 *
 * El problema que resuelven: los mismos datos se muestran en varios "almacenes"
 * de caché con claves distintas (lista de gastos, balances, resumen de inicio,
 * feed de actividad, analíticas). Invalidar solo el listado del grupo dejaba el
 * resto desactualizado hasta refrescar.
 *
 * Nota: usamos los PREFIJOS de query key como literales (raíces estables) en
 * vez de importar las *Keys de cada feature. Así este módulo compartido no
 * arrastra supabase (los api.ts lo cargan al evaluarse), evitando acoplar la
 * invalidación a la infra y romper tests que mockean supabase. Los prefijos
 * reflejan las factories: expensesKeys.byGroup=['expenses','group',gid],
 * debtsKeys.byGroup=['debts','group',gid], groupsKeys.byId=['groups','id',gid],
 * groupsKeys.all=['groups'], homeKeys.all=['home'], activityKeys.all=['activity'],
 * analyticsKeys.all=['analytics']. La invalidación es por prefijo, así que
 * ['debts','group',gid] alcanza simplified/cumulative/settlements.
 */

/**
 * Cambio que afecta a los datos financieros de un grupo (crear/editar/borrar
 * gasto, liquidación, alta/baja de miembro). Refresca TODAS las vistas derivadas.
 */
export function invalidateGroupFinancials(qc: QueryClient, groupId: string): void {
  void qc.invalidateQueries({ queryKey: ['expenses', 'group', groupId] });
  void qc.invalidateQueries({ queryKey: ['debts', 'group', groupId] });
  void qc.invalidateQueries({ queryKey: ['groups', 'id', groupId] });
  void qc.invalidateQueries({ queryKey: ['home'] });
  void qc.invalidateQueries({ queryKey: ['activity'] });
  void qc.invalidateQueries({ queryKey: ['analytics'] });
}

/**
 * Cambio en la entidad grupo (crear/editar/borrar/archivar/restaurar/salir):
 * listas de grupos, resumen de inicio (que también lista grupos) y actividad.
 */
export function invalidateGroupsList(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['groups'] });
  void qc.invalidateQueries({ queryKey: ['home'] });
  void qc.invalidateQueries({ queryKey: ['activity'] });
}
